import React, { useEffect, useState, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

// hooks
import { useHistory, useLocation } from "react-router-dom";
import useListSelector from "../../lib/useListSelector";

// components
import Sallie from "../UIComponents/Sallie";
import ListSelector from "../UIComponents/ListSelector";
import SubmitButton from "../UIComponents/SubmitButton";
import OnboardingStepHeader from "./OnboardingStepHeader";

// lib
import { Form, Col } from "react-bootstrap";

// api
import { createSmiHistory } from "../../api/smiRequests";

// NOTE: this file resolves its own patient/caregiver text directly off
// user.userType, the same way RecentSMI1.js/RecentSMI2.js do, rather
// than going through customizeMessages.

// NOTE: the medication question, "Compliant?" and tolerate columns use SMI-only
// recentsmi3.medication.* keys so the shared recentcondition2.medication.*
// copy used by RecentCondition2.js stays unchanged.

// Saves to POST /record-builder/smi-history (family history + meds,
// sets registration step3). The family history checklist below is
// hardcoded via translation keys
// rather than fetched from getConditionFormData, since that endpoint
// is Dementia/Condition-specific.
const RecentSMI3 = ({ setOnboardingPercent, user }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [returnedData, setReturnedData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);

  const history = useHistory();
  const location = useLocation();

  const [fields, setFields] = useState([
    { medication: "", compliant: "", tolerate: "" },
  ]);

  const handleAddField = () => {
    setFields([...fields, { medication: "", compliant: "", tolerate: "" }]);
  };

  const handleDeleteField = (index) => {
    const newFields = [...fields];
    newFields.splice(index, 1);
    setFields(newFields);
  };

  const handleChange = (event, index, field) => {
    const newFields = [...fields];
    newFields[index][field] = event.target.value;
    setFields(newFields);
  };

  const handleCompliantChange = (event, index) => {
    const newFields = [...fields];
    newFields[index].compliant = event.target.value;
    setFields(newFields);
  };

  const handleOptionChange = (event, index) => {
    const newFields = [...fields];
    newFields[index].tolerate = event.target.value;
    setFields(newFields);
  };

  const familyInfo = [
    {
      category: "familyHistory",
      items: Array.from({ length: 7 }, (_, i) =>
        t(`recentsmi3.family.options.${i}`)
      ),
    },
  ];

  const {
    toggleFromSelectedList,
    switchSingleSelection,
    addOther,
    selectedData,
    initialize,
  } = useListSelector({
    setInitialData: setReturnedData,
    initialData: returnedData,
  });

  const onSubmit = async () => {
    const assignedData = {
      familyHistoryOptions: selectedData.familyHistory,
      medications: fields,
    };
    setSubmitting(true);

    try {
      await createSmiHistory({
        patientId: patient.patientId,
        data: assignedData,
      });
    } catch (err) {
      console.error("createSmiHistory", err);
      setSubmitting(false);
      history.push("/oops");
      return;
    }

    setSubmitting(false);
    history.push("/laterSymptoms", {
      fromPatientControl: location.state?.fromPatientControl,
      date: location.state?.date,
    });
  };

  useEffect(() => {
    setReturnedData(familyInfo);
    initialize(familyInfo);
    setLoading(false);
  }, [t]);

  const isCaregiver = user?.userType === "caregiver";

  const getMessage = (key) => {
    const messages = {
      mainHeader: {
        patient: t("recentsmi3.header"),
        caregiver: t("recentsmi3.header"),
      },
      sallieText: {
        patient: t("recentsmi3.sallie.patient"),
        caregiver: t("recentsmi3.sallie.caregiver"),
      },
      sallieTip: {
        patient: t("recentsmi3.sallieTip.patient"),
        caregiver: t("recentsmi3.sallieTip.caregiver"),
      },
      familyHistory: {
        patient: t("recentsmi3.family.patient"),
        caregiver: t("recentsmi3.family.caregiver"),
      },
      medications: {
        patient: t("recentsmi3.medication.patient"),
        caregiver: t("recentsmi3.medication.caregiver"),
      },
    };
    return isCaregiver ? messages[key].caregiver : messages[key].patient;
  };

  const options = {
    familyHistory: { message: "familyHistory" },
  };

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <div className="d-block">
          <OnboardingStepHeader text={getMessage("mainHeader")} />
          <Sallie
            text={
              <>
                {getMessage("sallieText")}
                <br />
                {getMessage("sallieTip")}
              </>
            }
          />

          {returnedData.map((itemList, i) => {
            const { category } = itemList;
            return (
              <ListSelector
                key={category}
                category={category}
                message={getMessage(options[category].message)}
                list={itemList.items}
                index={i}
                maxSelectAmount={options[category].responseNumber}
                selectedList={selectedData[category]}
                toggleFromSelectedList={toggleFromSelectedList}
                selectOne={switchSingleSelection}
                addOther={addOther}
              />
            );
          })}

          <div className="listSelector">
            <Form.Label className={"h6-label"}>
              {getMessage("medications")}
            </Form.Label>

            {fields.map((field, index) => (
              <Form.Row key={index} className="listSelector">
                <Form.Group as={Col} md={3}>
                  <Form.Label>
                    {t("recentcondition2.medication.type")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t("recentcondition2.medication.enter")}
                    value={field.medication}
                    onChange={(event) =>
                      handleChange(event, index, "medication")
                    }
                  />
                </Form.Group>

                <Form.Group as={Col} md={3}>
                  <Form.Label>
                    {t("recentsmi3.medication.compliant")}
                  </Form.Label>
                  <Col>
                    <Form.Check
                      inline
                      label={t("recentsmi3.medication.compliantOptions.yes")}
                      type="radio"
                      name={`radio-compliant-${index}`}
                      id={`field-${index}-compliant-yes`}
                      value="yes"
                      checked={field.compliant === "yes"}
                      onChange={(event) => handleCompliantChange(event, index)}
                    />
                    <Form.Check
                      inline
                      label={t("recentsmi3.medication.compliantOptions.no")}
                      type="radio"
                      name={`radio-compliant-${index}`}
                      id={`field-${index}-compliant-no`}
                      value="no"
                      checked={field.compliant === "no"}
                      onChange={(event) => handleCompliantChange(event, index)}
                    />
                    <Form.Check
                      inline
                      label={t("recentsmi3.medication.compliantOptions.onOff")}
                      type="radio"
                      name={`radio-compliant-${index}`}
                      id={`field-${index}-compliant-onOff`}
                      value="onOff"
                      checked={field.compliant === "onOff"}
                      onChange={(event) => handleCompliantChange(event, index)}
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Col} md={5}>
                  <Form.Label>
                    {t("recentsmi3.medication.tolerate")}
                  </Form.Label>
                  <Col>
                    <Form.Check
                      inline
                      label={t("recentcondition2.medication.no_problems")}
                      type="radio"
                      name={`radio-option-${index}`}
                      id={`field-${index}-option-a`}
                      value="No Problems"
                      checked={field.tolerate === "No Problems"}
                      onChange={(event) => handleOptionChange(event, index)}
                    />
                    <Form.Check
                      className="pt-2"
                      inline
                      label={t("recentcondition2.medication.side_effects")}
                      type="radio"
                      name={`radio-option-${index}`}
                      id={`field-${index}-option-b`}
                      value="Experienced Side Effects"
                      checked={field.tolerate === "Experienced Side Effects"}
                      onChange={(event) => handleOptionChange(event, index)}
                    />
                    <Form.Check
                      className="pt-2"
                      inline
                      label={t("recentsmi3.medication.refused")}
                      type="radio"
                      name={`radio-option-${index}`}
                      id={`field-${index}-option-c`}
                      value="Refused to Take"
                      checked={field.tolerate === "Refused to Take"}
                      onChange={(event) => handleOptionChange(event, index)}
                    />
                  </Col>
                </Form.Group>
                <SubmitButton
                  variant="primary"
                  onClick={() => handleDeleteField(index)}
                >
                  X
                </SubmitButton>
              </Form.Row>
            ))}
            <Form.Row>
              <SubmitButton variant="primary" onClick={handleAddField}>
                +
              </SubmitButton>
            </Form.Row>
          </div>

          {errorMessage && (
            <Form.Control.Feedback
              style={{ display: "block" }}
              type="invalid"
            >
              {t("recentsmi3.choose.error")}
            </Form.Control.Feedback>
          )}
          <SubmitButton disabled={submitting} onClick={onSubmit} type="submit">
            {t("onboarding.saveContinue")} &gt;
          </SubmitButton>
        </div>
      )}
    </>
  );
};

export default RecentSMI3;